import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Upload, Copy, CheckCircle2, ShieldCheck, ArrowRight } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { INVESTMENT_TIER_META, api } from "../lib/api";
import { toast } from "sonner";

export default function InvestmentModal({ open, tier, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [idFile, setIdFile] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  const [amount, setAmount] = useState(0);
  const [crypto, setCrypto] = useState("BTC");
  const [wallets, setWallets] = useState({ btc_address: "", eth_address: "", usdt_address: "" });
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    if (open && tier) {
      setAmount(INVESTMENT_TIER_META[tier].amount);
      setStep(1);
      setIdFile(null);
      setProofFile(null);
      api.get("/settings").then((r) => setWallets(r.data));
    }
  }, [open, tier]);

  if (!tier) return null;

  const meta = INVESTMENT_TIER_META[tier];
  const roi = meta.roi;
  const minAmount = meta.amount;
  const maxAmount = meta.max;
  const addr = crypto === "BTC" ? wallets.btc_address : crypto === "ETH" ? wallets.eth_address : wallets.usdt_address;

  const isAmountValid = amount >= minAmount && (maxAmount == null || amount <= maxAmount);

  const copyAddr = () => {
    navigator.clipboard.writeText(addr);
    setCopied(crypto);
    setTimeout(() => setCopied(""), 1500);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/investments", { tier, amount, crypto, tx_proof: proofFile?.name || null });
      toast.success("Real investment submitted!", { description: "Your capital funding is under review. You will be notified within 30 minutes." });
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
          data-testid="investment-modal"
        >
          <motion.div
            initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="glass-strong rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto scrollbar-hide"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <div className="label-eyebrow">Real Investment - Step {step} of 3</div>
                <div className="font-display text-2xl font-bold mt-1">{meta.name} Real Investment</div>
              </div>
              <button onClick={onClose} data-testid="modal-close"><X className="w-5 h-5 text-white/60 hover:text-white" /></button>
            </div>

            <div className="px-6 py-6">
              {step === 1 && (
                <div className="space-y-5" data-testid="step-verify">
                  <div className="flex items-center gap-2 text-[#D4AF37]">
                    <ShieldCheck className="w-5 h-5" /> <span className="text-sm font-semibold">Identity Verification</span>
                  </div>
                  <p className="text-sm text-white/70">For security, please upload a clear photo of your government-issued ID or passport. Your data is encrypted end-to-end.</p>
                  <label className="block">
                    <div className="border-2 border-dashed border-white/15 rounded-xl p-8 text-center cursor-pointer hover:border-[#D4AF37]/50 transition-colors">
                      <Upload className="w-8 h-8 mx-auto text-[#D4AF37] mb-3" />
                      <div className="text-sm">{idFile ? idFile.name : "Click to upload ID / Passport"}</div>
                      <div className="text-xs text-white/40 mt-1">PNG, JPG, PDF up to 10MB</div>
                    </div>
                    <input type="file" className="hidden" onChange={(e) => setIdFile(e.target.files[0])} data-testid="id-upload" accept="image/*,.pdf" />
                  </label>

                  <div>
                    <label className="label-eyebrow block mb-2">Real Investment Amount (USD)</label>
                    <input
                      type="number"
                      min={minAmount}
                      max={maxAmount || undefined}
                      value={amount}
                      onChange={(e) => setAmount(Number(e.target.value))}
                      className="w-full px-4 py-3 rounded-lg"
                      data-testid="invest-amount"
                    />
                    <div className="text-xs text-white/50 mt-1">
                      {maxAmount
                        ? `Range $${minAmount.toLocaleString()} - $${maxAmount.toLocaleString()}`
                        : `Minimum $${minAmount.toLocaleString()} (no upper limit)`}
                      {" "}- expected return at {roi}% ROI: ${Math.round(amount * roi / 100).toLocaleString()} in 30 days
                    </div>
                    {!isAmountValid && (
                      <div className="text-xs text-red-400 mt-1">
                        {amount < minAmount
                          ? `Amount must be at least $${minAmount.toLocaleString()}`
                          : `Amount must be $${maxAmount.toLocaleString()} or less — choose the next tier up for more`}
                      </div>
                    )}
                  </div>

                  <button
                    disabled={!idFile || !isAmountValid}
                    onClick={() => setStep(2)}
                    className="btn-primary w-full disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="step1-next"
                  >
                    Verify & Continue <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-5" data-testid="step-payment">
                  <div className="flex items-center gap-2 text-[#00D68F]">
                    <CheckCircle2 className="w-5 h-5" /> <span className="text-sm font-semibold">Verified - Send Your Payment</span>
                  </div>
                  <p className="text-sm text-white/70">Send <span className="text-[#D4AF37] font-bold">${amount.toLocaleString()}</span> in crypto to one of the addresses below.</p>

                  <div className="flex gap-2">
                    {["BTC","ETH","USDT"].map(c => (
                      <button
                        key={c}
                        onClick={() => setCrypto(c)}
                        className={`flex-1 py-2 rounded-md border text-sm transition-colors ${crypto === c ? "bg-[#D4AF37] text-[#0A0E27] border-[#D4AF37]" : "border-white/10 text-white/70 hover:border-white/30"}`}
                        data-testid={`crypto-${c}`}
                      >
                        {c === "USDT" ? "USDT (TRC-20)" : c}
                      </button>
                    ))}
                  </div>

                  <div className="glass rounded-xl p-5 flex flex-col sm:flex-row gap-5 items-center">
                    <div className="bg-white p-3 rounded-lg">
                      <QRCodeSVG value={addr || "duocapital"} size={128} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="label-eyebrow mb-2">Wallet Address</div>
                      <div className="font-mono text-xs break-all bg-black/30 p-3 rounded">{addr}</div>
                      <button onClick={copyAddr} className="mt-3 flex items-center gap-2 text-sm text-[#D4AF37] hover:text-[#FCEB9E]" data-testid="copy-address">
                        {copied === crypto ? <><CheckCircle2 className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy address</>}
                      </button>
                    </div>
                  </div>

                  <div className="glass rounded-lg p-3 flex items-center gap-2 text-xs text-[#00D68F]">
                    <ShieldCheck className="w-4 h-4" /> Verified & Secured - Wallet rotates per session for security
                  </div>

                  <button onClick={() => setStep(3)} className="btn-primary w-full" data-testid="step2-next">I've sent the payment</button>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-5" data-testid="step-proof">
                  <div className="flex items-center gap-2 text-[#D4AF37]">
                    <Upload className="w-5 h-5" /> <span className="text-sm font-semibold">Submit Payment Proof</span>
                  </div>
                  <p className="text-sm text-white/70">Upload a screenshot of your transaction. Your real investment will be confirmed within 30 minutes.</p>
                  <label className="block">
                    <div className="border-2 border-dashed border-white/15 rounded-xl p-8 text-center cursor-pointer hover:border-[#D4AF37]/50 transition-colors">
                      <Upload className="w-8 h-8 mx-auto text-[#D4AF37] mb-3" />
                      <div className="text-sm">{proofFile ? proofFile.name : "Upload transaction screenshot"}</div>
                    </div>
                    <input type="file" className="hidden" onChange={(e) => setProofFile(e.target.files[0])} data-testid="proof-upload" accept="image/*" />
                  </label>

                  <button onClick={submit} disabled={submitting || !proofFile} className="btn-primary w-full disabled:opacity-40" data-testid="step3-submit">
                    {submitting ? "Submitting..." : "Confirm Real Investment"}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}